import React, { useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Select,
} from "@mui/material";
import CommentsTable from "./CommentsTable";
import { useParams, useSearchParams } from "react-router-dom";
import { CategoryLabels } from "../categoryLabels.enum";
import useRoleCheck from "../../../hooks/useRoleCheck";
import { localizePal } from "../../../utils/brand";
import { FilterLineIcon, PlusIcon } from "../../../components/common/Icons/Icons";

// URL params the filter panel owns (counted on the Filters button)
const filterParams = ['title', 'body', 'email', 'serial', 'category', 'unread', 'dev_only'];

interface ContainerProps {
  setAddOpen: any;
  setViewOpen: any;
  setDeleteOpen: any;
  setComment: any;
}

const CommentsContainer: React.FC<ContainerProps> = ({
  setAddOpen,
  setViewOpen,
  setDeleteOpen,
  setComment,
}) => {
  const { canAccess: isDeveloper } = useRoleCheck(['developer']);
  // Open by default: every filter in view
  const [showFilters, setShowFilters] = useState(true);
  //Filters
  const [title, setTitle] = useState(``);
  const [category, setCategory] = useState(``);
  const [unread, setUnread] = useState(false);
  const [devOnly, setDevOnly] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [body, setBody] = useState(``);
  const [email, setEmail] = useState(``);
  const [serial, setSerial] = useState(``);

  const {id} = useParams();
  
  useEffect(() => {
    setTitle(searchParams.get('title')??'')
    setCategory(searchParams.get('category')??'')
    setUnread(searchParams.get('unread')==='true')
    setDevOnly(searchParams.get('dev_only')==='true')
    setBody(searchParams.get('body')??'')
    setEmail(searchParams.get('email')??'')
    setSerial(searchParams.get('serial')??'')

  }, [searchParams]);

  const handleFilter = () => {
    searchParams.delete('page'); //reset page
    title==="" ? searchParams.delete('title') : searchParams.set('title', title);
    category==="" ? searchParams.delete('category') : searchParams.set('category', category);
    !unread ? searchParams.delete('unread') : searchParams.set('unread', 'true');
    !devOnly ? searchParams.delete('dev_only') : searchParams.set('dev_only', 'true');
    body==="" ? searchParams.delete('body') : searchParams.set('body', body);
    email==="" ? searchParams.delete('email') : searchParams.set('email', email);
    serial==="" ? searchParams.delete('serial') : searchParams.set('serial', serial);
    setSearchParams(searchParams);
  }

  const handleClear = () => {
    searchParams.delete('page'); //reset page
    setTitle("");
    searchParams.delete('title');

    setCategory("");
    searchParams.delete('category');

    setUnread(false);
    searchParams.delete('unread');

    setDevOnly(false);
    searchParams.delete('dev_only');

    setBody("");
    searchParams.delete('body');

    setEmail("");
    searchParams.delete('email');

    setSerial("");
    searchParams.delete('serial');

    setSearchParams(searchParams);
  }

  const activeFilters = filterParams.filter((param)=>searchParams.has(param)).length;

  const searchField = (fieldId: string, label: string, value: string, setValue: (v: string) => void, autoComplete: string) => (
    <TextField
      id={fieldId}
      label={label}
      type="search"
      fullWidth={true}
      value={value??""}
      onChange={(e)=>setValue(e.target.value)}
      onKeyDown={(e)=>(e.code==="Enter" ? handleFilter() : null)}
      size="small"
      autoComplete={autoComplete}
    />
  );

  const toggle = (label: string, value: boolean, onChange: (v: boolean) => void) => (
    <button type="button" className={`lp-toggle ${value ? 'on' : ''}`} aria-pressed={value} onClick={()=>onChange(!value)}>
      <span className="lp-toggle-box" aria-hidden="true"/>
      {label}
    </button>
  );

  const toolbar = (
    <>
      <button
        type="button"
        className={`btn btn-sm dt-tool-btn lp-filter-toggle ${showFilters ? 'active' : ''}`}
        onClick={()=>setShowFilters(!showFilters)}
        aria-expanded={showFilters}
        aria-controls="lp-filters"
      >
        <FilterLineIcon/> Filters
        {activeFilters > 0 && <span className="lp-count">{activeFilters}</span>}
      </button>

      {id &&
        <button type="button" className="btn btn-sm btn-primary lp-primary-action" onClick={()=>setAddOpen(true)}>
          <PlusIcon/> Add Comment
        </button>}
    </>
  );

  return (
    <div className={`lp-layout ${showFilters ? '' : 'filters-hidden'}`}>
      {showFilters && (
        <aside className="lp-filters" id="lp-filters" aria-label="Filters">
          <div className="lp-filters-body">
            <div className="lp-group">
              {searchField("title-search", "Title", title, setTitle, "comment-title")}
              {searchField("body-search", "Body", body, setBody, "comment-body")}
              {searchField("email-search", "Email", email, setEmail, "comment-email")}
              {!id && searchField("serial-search", "Spa Serial", serial, setSerial, "spa-comment-serial")}

              <FormControl fullWidth size="small">
                <InputLabel id="category-filter-label">Category</InputLabel>
                <Select
                  labelId="category-filter-label"
                  label="Category"
                  value={category}
                  onChange={e => setCategory(e.target.value as string)}
                >
                  <MenuItem value="">All</MenuItem>
                  {Object.keys(CategoryLabels).map((cat:any,i:number)=>
                    <MenuItem key={i} value={cat}>{localizePal(CategoryLabels[cat as keyof typeof CategoryLabels])}</MenuItem>
                  )}
                </Select>
              </FormControl>

              <div className="lp-toggles">
                {toggle("Unread", unread, setUnread)}
                {isDeveloper && toggle("Dev Only", devOnly, setDevOnly)}
              </div>
            </div>
          </div>

          <div className="lp-filters-foot">
            <button type="button" className="btn btn-sm lp-clear-btn" onClick={handleClear}>Clear</button>
            <button type="button" className="btn btn-sm btn-primary" onClick={handleFilter}>Apply Filters</button>
          </div>
        </aside>
      )}

      <CommentsTable
        setComment={setComment}
        setViewOpen={setViewOpen}
        setDeleteOpen={setDeleteOpen}
        toolbar={toolbar}
      />
    </div>
  );
};

export default CommentsContainer;
